import { createAsyncThunk } from "@reduxjs/toolkit";
import type {
  NormalizedSegments,
  RawCategory,
  Segment,
  SegmentCreateRequestPayload,
  SegmentUpdateRequestPayload,
} from "@interfaces/segments";
import userService from "@userService";
import { notifyAssistantSegmentsChanged } from "@/components/AIAssistant/utils/segmentsCacheInvalidation";
import { normalizeSegments } from "./utils";
import ShowToaster from "@/toaster";
import type { Category } from "../../../interfaces/segments";

export const fetchSegmentsByMarketId = createAsyncThunk<
  NormalizedSegments,
  { marketIds: number[]; marketCodes: string[] },
  { rejectValue: string }
>("segments/fetchByMarketId", async ({ marketIds, marketCodes }, thunkAPI) => {
  try {
    const response: RawCategory[] = (await userService.segments.getSegments(
      marketIds
    )) as any;
    return normalizeSegments(response as RawCategory[], marketCodes);
  } catch (err: any) {
    ShowToaster.error("Failed to fetch segments");
    return thunkAPI.rejectWithValue("Fetch segment failed " + err.message);
  }
});

export const createSegment = createAsyncThunk<
  { category: Category; segment: Segment } | null,
  { payload: SegmentCreateRequestPayload },
  { rejectValue: string }
>("segments/createSegment", async ({ payload }, thunkAPI) => {
  try {
    const response: RawCategory[] = (await userService.segments.create(
      payload
    )) as any;
    if (response.length > 0) {
      const { categories, segments } = normalizeSegments(
        response as RawCategory[]
      );
      if (categories?.length > 0 && segments?.length > 0) {
        try {
          await notifyAssistantSegmentsChanged({
            marketIds: payload.geoIds,
            op: "create",
            segmentId: segments[0].id,
          });
        } catch {
          //TODO: failure cases
        }
        return { category: categories[0], segment: segments[0] };
      }
    }
    return null;
  } catch (err: any) {
    ShowToaster.error("Failed to create segment.");
    return thunkAPI.rejectWithValue("Fetch segment failed " + err.message);
  }
});

export const updateSegment = createAsyncThunk<
  { category: Category; segment: Segment } | null,
  { segmentId: number; payload: SegmentUpdateRequestPayload },
  { rejectValue: string }
>("segments/updateSegment", async ({ payload, segmentId }, thunkAPI) => {
  try {
    const response: RawCategory[] = (await userService.segments.update(
      segmentId,
      payload
    )) as any;
    if (response.length > 0) {
      const { categories, segments } = normalizeSegments(
        response as RawCategory[]
      );
      if (categories?.length > 0 && segments?.length > 0) {
        try {
          await notifyAssistantSegmentsChanged({
            marketIds: payload.geoIds,
            op: "update",
            segmentId: segmentId,
          });
        } catch {
          //TODO: failure cases
        }
        return { category: categories[0], segment: segments[0] };
      }
    }
    return null;
  } catch (err: any) {
    ShowToaster.error("Failed to update segment.");
    return thunkAPI.rejectWithValue("Fetch segment failed " + err.message);
  }
});

export const deleteSegment = createAsyncThunk<
  { segmentId: number } | null,
  { segmentId: number; marketIds?: number[] },
  { rejectValue: string }
>("segments/deleteSegment", async ({ segmentId, marketIds }, thunkAPI) => {
  try {
    const response: string = (await userService.segments.delete(
      segmentId
    )) as any;
    if (!(response === "Done")) {
      ShowToaster.error("Failed to delete segment.");
    }
    if (response === "Done") {
      try {
        if (marketIds?.length) {
          await notifyAssistantSegmentsChanged({
            marketIds,
            op: "delete",
            segmentId,
          });
        }
      } catch {
        // best effort
      }
    }
    return response === "Done"
      ? {
          segmentId: segmentId,
        }
      : null;
  } catch (err: any) {
    ShowToaster.error("Failed to delete segment.");
    return thunkAPI.rejectWithValue("Failed to delete segment." + err.message);
  }
});

export const updateCategoryOrder = createAsyncThunk<
  { categoryId: number; position: number }[],
  number[],
  { rejectValue: string }
>("segments/category/updateOrder", async (categoryIds, thunkAPI) => {
  try {
    const requestPayload = categoryIds.map((categoryId, index) => ({
      categoryId,
      position: index,
    }));
    const response: string = (await userService.segments.updateCategoryOrder(
      requestPayload
    )) as any;
    return response === "Done" ? requestPayload : [];
  } catch (err: any) {
    ShowToaster.error("Fetch to update segments category order.");
    return thunkAPI.rejectWithValue(
      "Fetch to update segments category order. " + err.message
    );
  }
});

export const updateSegmentsOrder = createAsyncThunk<
  { segmentId: number; position: number }[],
  { segmentId: number; position: number }[],
  { rejectValue: string }
>("segments/updateOrder", async (requestPayload, thunkAPI) => {
  try {
    const response: string = (await userService.segments.updateSegmentOrder(
      requestPayload
    )) as any;
    return response === "Done" ? requestPayload : [];
  } catch (err: any) {
    ShowToaster.error("Failed to update segments order.");
    return thunkAPI.rejectWithValue(
      "Failed to update segments order. " + err.message
    );
  }
});

export const updateColor = createAsyncThunk<
  { segmentId: number; color: string } | null,
  { segmentId: number; color: string },
  { rejectValue: string }
>("segments/updateColor", async ({ segmentId, color }, thunkAPI) => {
  try {
    const response: string = (await userService.segments.updateSegmentColor(
      segmentId,
      color
    )) as any;
    return response === "Done" ? { segmentId, color } : null;
  } catch (err: any) {
    ShowToaster.error("Failed to update segment color.");
    return thunkAPI.rejectWithValue(
      "Failed to update segment color. " + err.message
    );
  }
});

export const updateStatus = createAsyncThunk<
  { segmentId: number; isActive: boolean }[] | null,
  { segmentId: number; isActive: boolean }[],
  { rejectValue: string }
>("segments/updateStatus", async (requestData, thunkAPI) => {
  try {
    const response: string = (await userService.segments.updateStatus(
      requestData
    )) as any;
    return response === "Done" ? requestData : null;
  } catch (err: any) {
    ShowToaster.success("Failed to update segment status.");
    return thunkAPI.rejectWithValue(
      "Failed to update segment status. " + err.message
    );
  }
});

export const updateCategoryName = createAsyncThunk<
  { categoryId: number; name: string } | null,
  { categoryId: number; name: string },
  { rejectValue: string }
>("segments/category/updateName", async ({ categoryId, name }, thunkAPI) => {
  try {
    const response: any = await userService.segments.updateCategoryName({
      categoryId,
      name,
    });
    if (response) {
      ShowToaster.success("Category updated successfully.");
      return { categoryId, name };
    }
    return null;
  } catch (err: any) {
    ShowToaster.error("Failed to update category name.");
    return thunkAPI.rejectWithValue(
      "Failed to update category name. " + err.message
    );
  }
});
